import { TOOL_NAMES } from 'js/src/agent/toolNames';
import { z } from 'zod';

import {
    type EnhancedToolResult,
    type ToolDefinition,
    type ToolExecutionContext,
} from '../../types';
import { DesignTool, type DesignToolArgs } from '../designTargeting';
import { checkpointDesign } from '../htmlAssetToolHelpers';
import { type ChartSceneCheck, checkChartScene } from './chartChecks';

import { SubagentAccess } from 'js/src/agent/subagents/subagentAccess';

export const writeSceneHtmlSchema = z.object({
    html: z.string().describe('The complete <!DOCTYPE html>…</html> document for the scene.'),
});
export type WriteSceneHtmlArgs = z.infer<typeof writeSceneHtmlSchema>;

const NOT_LOADED_PROBLEM =
    "The scene couldn't be loaded to check it, so it may not show at all. Make sure it's a complete document whose scripts run without errors.";

// A chart gets one retry: its second write that can't be shown ends the run.
const ATTEMPTS = 2;

export interface WriteSceneHtmlToolArgs extends DesignToolArgs {
    /** The chart's one scene: the tool writes nowhere else, so the subagent needs no scope. */
    sceneId: string;
}

/**
 * The chart subagent's one tool: applies its scene's whole HTML, then checks that it runs. A scene that
 * can't be loaded, never registers its timeline or renders nothing is refused with the reason and the
 * scene keeps the design it had, so the designer can write it once more; anything else ends the run.
 * The layout checks go in the tool message's record, which no model is sent.
 */
export class WriteSceneHtmlTool extends DesignTool<
    typeof writeSceneHtmlSchema,
    WriteSceneHtmlToolArgs
> {
    private readonly sceneId: string;
    // One tool per subagent run, so this counts that run's writes.
    private failedWrites = 0;

    public constructor(args: WriteSceneHtmlToolArgs) {
        super(args);
        this.sceneId = args.sceneId;
    }

    protected override readonly schema = writeSceneHtmlSchema;
    protected override readonly toolName = TOOL_NAMES.writeSceneHtml;
    public override readonly subagentAccess = SubagentAccess.EDITS_TARGET;
    protected override readonly toolDescription =
        "Replace the scene's whole HTML document with this one. It is checked that it runs; if it " +
        "doesn't, you're told why.";

    // Bound to its scene, so it takes no scene_id.
    protected override extendParameters(
        parameters: ToolDefinition['function']['parameters'],
    ): ToolDefinition['function']['parameters'] {
        return parameters;
    }

    public getToolDisplayName(): string {
        return 'Write chart scene';
    }

    public get statusIcon(): string {
        return 'code';
    }

    public async execute(
        args: WriteSceneHtmlArgs,
        context: ToolExecutionContext,
    ): Promise<EnhancedToolResult> {
        const resolved = this.resolveAuthoringTarget(this.sceneId);
        if ('error' in resolved) {
            return resolved.error;
        }
        const { asset } = resolved;
        const previousHtml = asset.html;
        this.luminary.batchRun(() => {
            asset.html = args.html;
        });

        const { width, height } = this.luminary.referenceResolution;
        const check = await asset
            .introspectComposition((composition) => checkChartScene(composition, { width, height }))
            .then((checked) => checked ?? { problem: NOT_LOADED_PROBLEM, layout: null })
            .catch(
                (error: unknown): ChartSceneCheck => ({
                    problem: `${NOT_LOADED_PROBLEM} (${error instanceof Error ? error.message : 'unknown error'})`,
                    layout: null,
                }),
            );
        if (check.problem) {
            this.luminary.batchRun(() => {
                asset.html = previousHtml;
            });
            this.failedWrites += 1;
            return {
                result: `Not applied, because it can't be shown: ${check.problem}`,
                isSuccess: false,
                ...(this.failedWrites >= ATTEMPTS ? { stopConversation: true } : {}),
            };
        }
        checkpointDesign(this.luminary, this.dispatch, context.parentActionGroup);
        return {
            result: 'Applied.',
            isSuccess: true,
            stopConversation: true,
            ...(check.layout ? { record: { layout: check.layout } } : {}),
        };
    }
}
